import { useEffect, useState } from "react";
import { m } from "@/paraglide/messages";

interface TocEntry {
  id: string;
  text: string;
  level: number;
}

export function BlogTOC({ entries }: { entries: TocEntry[] }) {
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    const headings = entries
      .map((e) => document.getElementById(e.id))
      .filter(Boolean) as HTMLElement[];

    if (headings.length === 0) return;

    const observer = new IntersectionObserver(
      (intersections) => {
        for (const entry of intersections) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
            break;
          }
        }
      },
      { rootMargin: "-80px 0px -70% 0px", threshold: 0 },
    );

    for (const heading of headings) {
      observer.observe(heading);
    }

    return () => observer.disconnect();
  }, [entries]);

  if (entries.length === 0) return null;

  return (
    <nav
      className="sticky top-24 rounded-xl border border-border/30 bg-card/50 p-5 backdrop-blur-sm"
      aria-label={m.blog_table_of_contents()}
    >
      <h3 className="font-display mb-4 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
        {m.blog_table_of_contents()}
      </h3>
      <ul className="space-y-0.5">
        {entries.map((entry) => {
          const isActive = activeId === entry.id;
          return (
            <li key={entry.id}>
              <a
                href={`#${entry.id}`}
                className={`relative block rounded-md py-1.5 text-[13px] leading-snug transition-all duration-200 ${
                  entry.level === 3 ? "pl-7" : "pl-3.5"
                } ${
                  isActive
                    ? "bg-primary/8 font-medium text-primary"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                }`}
              >
                {/* Active indicator dot */}
                {isActive && (
                  <span className="absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-full bg-gradient-to-b from-primary via-accent to-secondary" />
                )}
                {entry.text}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
